import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../../../abstract/api-error';
import { OrganizationService } from '../../../../core/data-services/organization.service';
import { UnassignedTicketsService } from '../../../../core/data-services/unassigned-tickets.service';
import { UnassignedTicket } from '../../../../core/data-models/unassigned-ticket.model';
import { OrganizationOption } from '../../../../core/response-models/organization-option.model';

/**
 * Assign Ticket Modal Component
 *
 * Modal for assigning an unassigned support ticket to an organization.
 * Displays a searchable organization dropdown and optional profile ID field.
 *
 * @see agent-os/specs/2026-01-23-unassigned-tickets/spec.md
 */
@Component({
  selector: 'app-assign-ticket-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './assign-ticket-modal.component.html',
  styleUrls: ['./assign-ticket-modal.component.scss']
})
export class AssignTicketModalComponent implements OnInit {
  @Input() ticket!: UnassignedTicket;
  @Output() assigned = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  organizations: OrganizationOption[] = [];
  filteredOrganizations: OrganizationOption[] = [];
  selectedOrganizationId = '';
  profileId = '';
  searchQuery = '';
  loading = false;
  loadingOrganizations = true;
  errorMessage = '';

  constructor(
    private unassignedTicketsService: UnassignedTicketsService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit(): void {
    this.loadOrganizations();
  }

  /**
   * Load all organizations for the dropdown
   */
  async loadOrganizations(): Promise<void> {
    this.loadingOrganizations = true;

    try {
      const page = await firstValueFrom(this.organizationService.getOptions());
      this.organizations = page.items;
      this.filteredOrganizations = [...this.organizations];
    } catch (error) {
      console.error('Failed to load organizations:', error);
      this.errorMessage = 'Failed to load organizations.';
    } finally {
      this.loadingOrganizations = false;
    }
  }

  /**
   * Filter organizations based on search query
   */
  onSearchChange(): void {
    if (!this.searchQuery.trim()) {
      this.filteredOrganizations = [...this.organizations];
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredOrganizations = this.organizations.filter(org =>
      org.name.toLowerCase().includes(query)
    );
  }

  /**
   * Handle form submission to assign the ticket
   */
  async onAssign(): Promise<void> {
    if (!this.selectedOrganizationId) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      await this.unassignedTicketsService.assignTicket(
        this.ticket.id,
        this.selectedOrganizationId,
        this.profileId || undefined
      );
      this.assigned.emit();
    } catch (error) {
      console.error('Failed to assign ticket:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to assign ticket. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Close the modal
   */
  onCancel(): void {
    this.closed.emit();
  }
}
